import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IndentDetail } from '../Issue/Issue.helper';
import { ReportFooterTimestamp } from '../../../../utils/reportUtils';
import { convertUnderscoreToTitle } from '../../../../utils/commonUtils';
import DetailRow from '../common/DetailRow';

interface ReceiveReportData {
  id: number | string;
  [key: string]: unknown;
  indent_details?: IndentDetail[];
}

interface ReceiveReportProps {
  data: ReceiveReportData | null;
}

const ReceiveReport: React.FC<ReceiveReportProps> = ({ data }) => {
  const { t: tStockTransfer } = useTranslation('stockTransfer_config');
  
  if (!data) {
    return null;
  }

  const rowData = data as Record<string, unknown>;

  // Parse compound fields from the row data
  const receivedNoDate = typeof rowData.receivedNoDate === 'string' ? rowData.receivedNoDate : '';
  const [receiveNo, receiveDate] = receivedNoDate?.split(' | ') || ['-', '-'];
  
  const issueNoDate = typeof rowData.issueNoDate === 'string' ? rowData.issueNoDate : '';
  const [issueNo, issueDate] = issueNoDate?.split(' | ') || ['-', '-'];
  
  const indentNoDate = typeof rowData.indentNoDate === 'string' ? rowData.indentNoDate : '';
  const [indentNo, indentDate] = indentNoDate?.split(' | ') || ['-', '-'];
  
  // fromClinicReceivedStore contains "From Clinic | Received Store" (destination - TO store)
  const fromClinicReceivedStore = typeof rowData.fromClinicReceivedStore === 'string' ? rowData.fromClinicReceivedStore : '';
  const [toClinic, toStore] = fromClinicReceivedStore?.split(' | ') || ['-', '-'];
  
  // toClinicIssuedStore contains "To Clinic | Issued Store" (source - FROM store)
  const toClinicIssuedStore = typeof rowData.toClinicIssuedStore === 'string' ? rowData.toClinicIssuedStore : '';
  const [fromClinic, fromStore] = toClinicIssuedStore?.split(' | ') || ['-', '-'];
  
  const patient = typeof rowData.patient === 'string' ? rowData.patient : '-';
  const remark = typeof rowData.remark === 'string' ? rowData.remark : '';
  const createdBy = typeof rowData.created_by === 'string' ? rowData.created_by : '';
  // const approvedBy = typeof rowData.approved_by === 'string' ? rowData.approved_by : '';
  const itemDetails = Array.isArray(rowData.indent_details) ? rowData.indent_details : [];

  return (
    <Box sx={{ padding: 3, backgroundColor: '#fff' }}>
      {/* Receive Details Heading */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        {tStockTransfer('Receive Details')}
      </Typography>

      {/* Receive Details Section - Grid Layout */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'auto 1fr auto 1fr', 
        gap: 2, 
        mb: 4,
        backgroundColor: '#f9f9f9',
        padding: 2,
        borderRadius: 1
      }}>
        <DetailRow label={tStockTransfer('Receive No:')} value={receiveNo} />
        <DetailRow label={tStockTransfer('Receive Date:')} value={receiveDate} />
        
        <DetailRow label={tStockTransfer('Issue No:')} value={issueNo} />
        <DetailRow label={tStockTransfer('Issue Date:')} value={issueDate} />
        
        <DetailRow label={tStockTransfer('Indent No:')} value={indentNo} />
        <DetailRow label={tStockTransfer('Indent Date:')} value={indentDate} />
        
        <DetailRow 
          label={tStockTransfer('From Store:')} 
          value={`${fromClinic} - ${fromStore}`} 
        />
        <DetailRow 
          label={tStockTransfer('To Store:')} 
          value={`${toClinic} - ${toStore}`}
        />

        {patient && patient !== '-' && (
          <DetailRow 
            label={tStockTransfer('Patient Name:')} 
            value={patient}
          />
        )}
        {remark && remark !== '' && (
          <DetailRow 
            label={`${tStockTransfer('Remark')}:`} 
            value={remark}
            // span={3}
          />
        )}
        {createdBy && (
          <DetailRow 
            label={tStockTransfer('Received By:')} 
            value={createdBy}
          />
        )}
        {/* {approvedBy && (
          <DetailRow 
            label={tStockTransfer('Approved By:')} 
            value={approvedBy}
          />
        )} */}
      </Box>

      {/* Received Items Heading */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        {tStockTransfer('Received Items Details')}
      </Typography>

      {/* Items Table */}
      <Table size="small" sx={{ 
        border: '1px solid #e0e0e0',
        '& .MuiTableCell-root': {
          fontSize: '13px',
          borderRight: '1px solid #e0e0e0',
          '&:last-child': {
            borderRight: 'none'
          }
        }
      }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 600 }}>{tStockTransfer('Item Name')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{tStockTransfer('Batch No | Expiry Date')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{tStockTransfer('Quantity')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{tStockTransfer('SGST % | Amt.')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{tStockTransfer('CGST % | Amt.')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{tStockTransfer('Cost Amount')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{tStockTransfer('MRP Amount')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itemDetails?.map((item: IndentDetail, index: number) => {
            const sgstPercent = item.Taxes?.sgst?.percent ?? 0;
            const sgstAmount = item.Taxes?.sgst?.amount ?? 0;
            const cgstPercent = item.Taxes?.cgst?.percent ?? 0;
            const cgstAmount = item.Taxes?.cgst?.amount ?? 0;
            return (
              <TableRow key={index} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                <TableCell>{convertUnderscoreToTitle(item.item?.name_label ?? item.item?.name ?? '-')}</TableCell>
                <TableCell>{`${item.batch?.batch_number ?? '-'} | ${item.batch?.expiry_date ?? '-'}`}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.3 }}>
                      {tStockTransfer('Received')}: <strong>{item.received_qty ?? 0}</strong> ({item.uom_label ?? item.uom ?? '-'})
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.3 }}>
                      {tStockTransfer('Indent')}: <strong>{item.indent_qty ?? 0}</strong> ({item.uom_label ?? item.uom ?? '-'})
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                      {tStockTransfer('Issued')}: <strong>{item.issue_qty ?? 0}</strong> ({item.uom_label ?? item.uom ?? '-'})
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.3 }}>
                      {tStockTransfer('Percent')}: <strong>{sgstPercent}%</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                      {tStockTransfer('Amount')}: <strong>{sgstAmount}</strong>
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.3 }}>
                      {tStockTransfer('Percent')}: <strong>{cgstPercent}%</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                      {tStockTransfer('Amount')}: <strong>{cgstAmount}</strong>
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.3 }}>
                      {tStockTransfer('Base Cost')}: <strong>{item.batch?.cost_price ?? item.base_cost_price ?? 0}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                      {tStockTransfer('Total Cost Amount')}: <strong>{item.total_base_cost_price ?? 0}</strong>
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.3 }}>
                      {tStockTransfer('Base MRP')}: <strong>{item.batch?.mrp ?? item.base_mrp ?? 0}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                      {tStockTransfer('Total MRP Amount')}: <strong>{item.total_mrp_cost_price ?? 0}</strong>
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      
      <Box sx={{ borderTop: '1px solid #ddd', mt: 2, pt: 2 }}>
        <ReportFooterTimestamp />
      </Box>
    </Box>
  );
};

export default ReceiveReport;
